import { Alert, Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { useState } from 'react';
import { FormValuesB2B } from '../FormPageB2B';
import { isValidNIP } from '../helpers/validation';
import { dictionaryService } from '../services/dictionaryService';
import { useLanguage } from '../i18n/LanguageContext';
import { pl, TranslationKey } from '../i18n/translations';

// Do formularza (i dalej do GUS/FormsPortal) trafia polska nazwa formy prawnej — wartość
// z pl-słownika. Tłumaczenie dotyczy wyłącznie etykiety widocznej w liście wyboru.
const LEGAL_FORM_KEYS: TranslationKey[] = [
    'lf.jdg',
    'lf.spolka_zoo',
    'lf.spolka_cywilna',
    'lf.spolka_jawna',
    'lf.spolka_partnerska',
    'lf.spolka_komandytowa',
    'lf.spolka_komandytowo_akcyjna',
    'lf.prosta_spolka_akcyjna',
    'lf.spolka_akcyjna',
    'lf.stowarzyszenie',
    'lf.fundacja',
];

const CompanyData = ({ formik }: { formik: FormikProps<FormValuesB2B> }) => {
    const { values, handleChange, handleBlur, errors, touched, setFieldValue } = formik;
    const { t } = useLanguage();
    const [showGusDialog, setShowGusDialog] = useState(false);
    const [gusLoading, setGusLoading] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState<{ key: TranslationKey; severity: 'success' | 'warning' | 'error' } | null>(null);

    const formOptions = LEGAL_FORM_KEYS.map((key) => ({ key, plLabel: pl[key] }));

    const handleNipBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        handleBlur(e);
        const nip = e.target.value.replace(/\D/g, '');
        if (isValidNIP(nip)) {
            setShowGusDialog(true);
        }
    };

    const handleGusConfirm = async () => {
        setGusLoading(true);
        try {
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('timeout')), 3000)
            );
            const data = await Promise.race([
                dictionaryService.getNipData(values.nip),
                timeoutPromise,
            ]);
            if (data.name) setFieldValue('companyName', data.name);
            if (data.regon) setFieldValue('regon', data.regon);
            if (data.city) setFieldValue('city', data.city);
            if (data.postalCode) setFieldValue('postalCode', data.postalCode);
            if (data.street) setFieldValue('street', data.street);
            if (data.houseNumber) setFieldValue('houseNumber', data.houseNumber.substring(0, 6));
            if (data.legalForm) setFieldValue('legalForm', data.legalForm);
            setShowGusDialog(false);
            setSnackbarMsg({ key: 'gus.loaded', severity: 'success' });
        } catch (err) {
            setShowGusDialog(false);
            if (err instanceof Error && err.message === 'timeout') {
                setSnackbarMsg({ key: 'gus.timeout', severity: 'warning' });
            } else {
                setSnackbarMsg({ key: 'gus.notFound', severity: 'error' });
            }
        } finally {
            setGusLoading(false);
        }
    };

    return (
        <>
            <Grid container spacing={6} alignItems="flex-start" direction={{ xs: 'column', sm: 'row' }} sx={{ marginTop: '60px' }}>
                <Grid size={{ xs: 12, sm: 12, md: 5 }} sx={{ height: 'auto' }}>
                    <Typography variant="h6" sx={{ margin: 0, padding: '0px' }}>{t('section.companyData')}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 7 }}>
                    <Stack>
                        <Grid container spacing={4} direction={{ xs: 'column', sm: 'row' }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label={t('field.companyName')}
                                    name="companyName"
                                    value={values.companyName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.companyName && Boolean(errors.companyName)}
                                    helperText={touched.companyName && errors.companyName}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label={t('field.nip')}
                                    name="nip"
                                    value={values.nip}
                                    onChange={handleChange}
                                    onBlur={handleNipBlur}
                                    error={touched.nip && Boolean(errors.nip)}
                                    helperText={touched.nip && errors.nip}
                                    type="tel"
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label={t('field.regon')}
                                    name="regon"
                                    value={values.regon}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.nip && Boolean(errors.regon)}
                                    helperText={touched.nip && errors.regon}
                                    type="tel"
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Autocomplete
                                    disablePortal
                                    options={formOptions}
                                    getOptionLabel={(option) => t(option.key)}
                                    value={formOptions.find(opt => opt.plLabel === values.legalForm) || null}
                                    onChange={(_, newValue) => setFieldValue('legalForm', newValue?.plLabel || '')}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('field.legalForm')}
                                            name="legalForm"
                                            onBlur={handleBlur}
                                            error={touched.legalForm && Boolean(errors.legalForm)}
                                            helperText={touched.legalForm && errors.legalForm}
                                        />
                                    )}
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                        </Grid>
                    </Stack>
                </Grid>
            </Grid>

            <Dialog open={showGusDialog} onClose={() => setShowGusDialog(false)}>
                <DialogTitle>{t('gus.title')}</DialogTitle>
                <DialogContent>
                    {t('gus.question')}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowGusDialog(false)} disabled={gusLoading}>
                        {t('gus.no')}
                    </Button>
                    <Button onClick={handleGusConfirm} variant="contained" disabled={gusLoading}
                        startIcon={gusLoading ? <CircularProgress size={16} color="inherit" /> : null}>
                        {gusLoading ? t('gus.loading') : t('gus.yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!snackbarMsg}
                autoHideDuration={6000}
                onClose={() => setSnackbarMsg(null)}
            >
                {snackbarMsg ? (
                    <Alert onClose={() => setSnackbarMsg(null)} severity={snackbarMsg.severity} variant="filled" sx={{ width: '100%' }}>
                        {t(snackbarMsg.key)}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </>
    );
};

export default CompanyData;
